import { call, delay, fork, put, race, select, take, takeEvery, takeLatest } from 'redux-saga/effects';
import { loginActions, loginSelectors, loginTypes } from './index';
import { AccountInfoType, JWTTokenInfoType, LoginType, LoginWithTokenType, LoginWithUsernameType, TokenMeta } from './login.types';
import axios from 'axios';
import jwtDecode from 'jwt-decode';
import { applicationSelectors } from '../application';
import { push } from 'redux-first-history';
import { jobsActions } from '../jobs';
import { notificationActions } from '../notification';
import { communicationActions } from '../communication';

function* refreshToken(loginId: string) {
    let loggedIn = true;
    while (loggedIn) {
        let login: LoginType = yield select(loginSelectors.getLoginById(loginId));
        if (!login || !login.tokenInfo) {
            yield take(loginTypes.LOGIN_JWT_TOKEN_RECEIVED);
            continue;
        }

        let expiresIn = login.tokenInfo.exp - Math.floor(new Date().getTime() / 1000);

        if (expiresIn > 0) {
            let { logout } = yield race({
                wait: delay((expiresIn - 60) * 1000),
                logout: take(loginTypes.LOGOUT),
            });
            if (logout) {
                return;
            }
        }
        try {
            yield call(loadToken, login.refreshToken, login.tokenInfo.sub);
        } catch (e) {
            loggedIn = false;
        }
    }
}

export function* getToken(accountId: string, allowRetry = true) {
    let login: LoginType = yield select(loginSelectors.getLoginById(accountId));

    while (!login || !login.tokenInfo) {
        yield race({
            jwt: take(loginTypes.LOGIN_JWT_TOKEN_RECEIVED),
            addAccount: take(loginTypes.ADD_ACCOUNT),
        });
        login = yield select(loginSelectors.getLoginById(accountId));
    }

    let expiresIn = login.tokenInfo.exp - Math.floor(new Date().getTime() / 1000);
    // console.log("token expires in: ", expiresIn, " seconds")
    if (expiresIn < 30) {
        let { token } = yield call(loadToken, login.refreshToken, login.tokenInfo.sub, allowRetry);
        // console.log(`token was refreshed: ${token}`)
        return token as string;
    }
    return login.token as string;
}

function* getFreshToken(action: { type: string; payload: { accountId: string | undefined }; meta: TokenMeta }) {
    const { accountId } = action.payload;
    const { resolve, reject } = action.meta || {};

    try {
        if (accountId) {
            const token: string = yield call(getToken, accountId);

            return resolve?.(token);
        } else {
            return reject?.('Error in getFreshToken: No accountId provided!');
        }
    } catch (err) {
        console.error('getToken failed', err);
        return reject?.(err);
    }
}

function* hasSelfParent(parentLogin: string, accountId: string, jwtToken: string) {
    let accountInfo: AccountInfoType = yield call(getAccountInfoForToken, jwtToken);

    if (accountInfo.parentId) {
        let existingLogins: Array<[string, LoginType]> = yield select(loginSelectors.getLogins);

        let parent = existingLogins.find(([_accountId, login]) => {
            return login.info?.id === accountInfo.parentId;
        });

        if (parent) {
            yield put(push(`/management/account/${parent[0]}/${accountInfo.id}`));
            return true;
        }
        return false;
    } else {
        return false;
    }
}

function* tokenLogin({ token }: LoginWithTokenType) {
    try {
        let {
            tokenInfo,
            token: jwtToken,
            accountId,
            refreshToken: refreshTokenData,
        }: {
            tokenInfo: JWTTokenInfoType;
            token: string;
            accountId: string;
            refreshToken: string;
        } = yield call(loadToken, token, undefined);

        let selfParent: boolean = yield call(hasSelfParent, tokenInfo.sub, tokenInfo['envago:ksaAccountId'], jwtToken);
        if (selfParent) {
            console.log('This Account is a child of its own :)');
        } else {
            yield put(loginActions.addAccount(accountId, jwtToken, refreshTokenData, tokenInfo));

            yield fork(refreshToken, tokenInfo['envago:ksaAccountId']);
        }
    } catch (e) {
        if (e.response) {
            console.log(e.response);
        } else {
            console.log(e);
        }
        yield put(loginActions.loginWithTokenError('Zugang ungültig oder abgelaufen'));
    }
}

function* usernameLogin({ username, password }: LoginWithUsernameType) {
    const applicationId: string = yield select(applicationSelectors.getApplicationId);
    try {
        let { data } = yield axios.post(
            `/auth/token`,
            {
                applicationId: applicationId,
                username: username?.replace(/\s/g, ''),
                password: password?.replace(/\s/g, ''),
            },
            {},
        );

        let tokenInfo: JWTTokenInfoType = jwtDecode(data.token);

        yield put(loginActions.addAccount(data.username, data.token, data.refreshToken, tokenInfo));

        return tokenInfo;
    } catch (e) {
        console.log('LoginError', e);
        yield put(loginActions.loginWrongCredentials());
    }
}

export function* loadToken(token: string, accountId: undefined | string, retryAllowed = true) {
    const applicationId: string = yield select(applicationSelectors.getApplicationId);

    while (true) {
        try {
            let { data } = yield axios.post(
                `/auth/token`,
                {
                    applicationId: applicationId,
                    token: token,
                },
                {},
            );

            let tokenInfo: JWTTokenInfoType = jwtDecode(data.token);

            yield put(loginActions.loginJwtTokenReceived(data.username, data.token, data.refreshToken, tokenInfo));

            return {
                accountId: data.username,
                token: data.token,
                refreshToken: data.refreshToken,
                tokenInfo,
            };
        } catch (e) {
            if (e.response) {
                console.log('Response', e.response);
                if (accountId) {
                    yield put(loginActions.removeAccount(accountId));
                }
                throw e;
            }
            console.log(navigator.onLine, 'is online?');
            console.log('Error fetching token, will retry');
            if(!retryAllowed) {
                throw e;
            }
            console.log(
                'Retrying in 30 seconds, because of network error or expired token (will retry until 30 seconds have passed, then it will throw the error again)',
            )
            yield delay(30000);
        }
    }
}

function* logout() {
    yield put(communicationActions.resetCommunicationState());

    yield put(push('/'));
}

function* loadAccountInfo({ account }: { account: string; type: string }) {
    try {
        let token: string = yield call(getToken, account);

        let accountInfo: AccountInfoType = yield call(getAccountInfoForToken, token);

        yield put(loginActions.accountInformationReceived(account, accountInfo));
    } catch (e) {
        console.log(e);
    }
}

function* getAccountInfoForToken(token: string) {
    let { data: accountInfo }: { data: AccountInfoType } = yield axios.get(`/api/pro/account/info`, {
        headers: {
            Authorization: `Bearer ${token}`,
        },
    });
    return accountInfo;
}

function* watchLoginProcess() {
    try {
        while (true) {
            yield race({
                tokenLogin: take(loginTypes.TOKEN_LOGIN),
                userPasswordLogin: take(loginTypes.USER_PASSWORD_LOGIN),
            });

            const { account, error } = yield race({
                account: take(loginTypes.ADD_ACCOUNT),
                error: take(loginTypes.LOGIN_WRONG_CREDENTIALS),
            });

            if (error || !account?.account) {
                console.log('watchLoginProcess error or no account: ', error, account);
                continue;
            }

            const isMultipleAccountLoginEnabled: boolean = yield select(applicationSelectors.isMultipleAccountsLoginEnabled);

            if (!isMultipleAccountLoginEnabled) {
                const existingLogins: Array<[string, LoginType]> = yield select(loginSelectors.getLogins);

                if (existingLogins?.length > 0) {
                    for (const [accountId] of existingLogins) {
                        if (accountId !== account.account) {
                            yield put(loginActions.removeAccount(accountId));
                        }
                    }
                }
            }

            yield put(communicationActions.resetCommunicationState());
            yield put(loginActions.fetchAccountInformation(account.account));
            yield put(jobsActions.loadJobs());

            const isRedirectToJobsScreenOnInitEnabled: boolean = yield select(applicationSelectors.isRedirectToJobsScreenOnLoginEnabled);

            if (isRedirectToJobsScreenOnInitEnabled) {
                yield put(push('/ablesung'));
            } else {
                yield put(push('/'));
            }
        }
    } catch (err) {
        console.log('error in watchLoginProcess: ', err);
    }
}

function* checkLogins() {
    /*
    we delay the check-login a bit: if token-login is currently
     */
    yield delay(2000);

    let logins: LoginType[] = yield select(loginSelectors.getLogins);

    if (logins.length === 0) {
        console.log('All Logins expired');
        yield put(loginActions.logout());
    }
}

function* cleanRemovedAccounts() {
    let existingLogins: Array<[string, LoginType]> = yield select(loginSelectors.getLogins);
    for (let [accountId, login] of existingLogins) {
        if (login.refreshToken === undefined && login.token === undefined) {
            yield put(loginActions.removeAccount(accountId));
        }
    }
}

function* showAccountRemovedNotification() {
    yield put(notificationActions.showInfoNotification('Zugang abgelaufen', 'Ein abgelaufener Zugang wurde entfernt'));
}

export default function* loginSaga() {
    console.log('LoginSaga Ready!');

    yield call(cleanRemovedAccounts);

    yield takeLatest(loginTypes.TOKEN_LOGIN, tokenLogin);
    yield takeLatest(loginTypes.USER_PASSWORD_LOGIN, usernameLogin);
    yield takeLatest(loginTypes.LOGOUT, logout);
    yield takeEvery(loginTypes.FETCH_ACCOUNT_INFO, loadAccountInfo);
    yield takeLatest(loginTypes.GET_FRESH_TOKEN, getFreshToken);

    yield fork(watchLoginProcess);

    yield takeLatest(loginTypes.REMOVE_ACCOUNT, checkLogins);

    const isMultipleAccountLoginEnabled: boolean = yield select(applicationSelectors.isMultipleAccountsLoginEnabled);
    if (isMultipleAccountLoginEnabled) {
        yield takeLatest(loginTypes.REMOVE_ACCOUNT, showAccountRemovedNotification);
    }

    const loginIds: string[] = yield select(loginSelectors.getLoginIds);
    for (const loginId of loginIds) {
        yield fork(refreshToken, loginId);
        yield put(loginActions.fetchAccountInformation(loginId));
    }

    yield put(loginActions.loginInitializationDone());
}
